import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { HtmlSanitized } from 'components/display/HtmlSanitized';
import { EyeIcon } from 'components/icons/Icons';
import { Shield } from 'lucide-react';
import { contractsAPI } from 'services/swagger-api';
import { useContractInfosState } from '../ContractInfosContext';
import { GenericLoading } from 'components/ui/Loader';
import { CardsSection } from 'components/layout/Section/CardsSection';
import { Button } from 'components/ui/Button';
import { DropdownButton, useActionMenu } from 'components/navigation/Dropdown/GenericDropdownButton';
import { ContractGuaranteeDto } from 'api/contract';
import { useDialog } from 'hooks/useDialog';
import { Sizes } from 'components/forms/NumberSelector';

const ContractGuarantees: React.FC = () => {
  const { contractId } = useContractInfosState()
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [guarantees, setGuarantees] = useState<ContractGuaranteeDto[]>([])
  const { createMenuItem } = useActionMenu();
  const { openDialog } = useDialog();

  const mandatoryGuarantees = useMemo(() => {
    return guarantees.filter(g => g.isRequired)
  }, [guarantees])

  const optionnalGuarantees = useMemo(() => {
    return guarantees.filter(g => !g.isRequired)
  }, [guarantees])

  const showGuaranteeDetails = useCallback(
    (title: string, longDescription: string) => {
      openDialog({
        title,
        size: Sizes["4xl"],
      },
        <HtmlSanitized html={longDescription} className="font-12 text-dark-light w-100" />
      );
    }, [openDialog]
  );

  useEffect(() => {
    const getGuarantees = async () => {
      try {
        setIsLoading(true)
        const response = await contractsAPI.getGuarantees({ contractId })
        if (response) {
          setGuarantees(response.guarantees)
        }
      } catch (error) {
        console.error('Erreur lors de la récupération des devis:', error)
      } finally {
        setIsLoading(false)
      }
    }
    getGuarantees()
  }, [contractId])

  const moreMenu = useMemo(() => ({
    actions: [
      {
        options: {
          icon: 'arrowRightLeft',
        },
        key: 'test',
        label: 'Test',
        action: (row: ContractGuaranteeDto) => {
        }
      },
    ]
  }), []);

  if (!guarantees || isLoading) {
    return (
      <div className="relative flex justify-center items-center p-8">
        <GenericLoading isParentSized />
      </div>
    );
  }


  // Icônes par type de garantie (basé sur le nom pour l'exemple)
  const getIcon = (name: string): JSX.Element => {
    return <Shield className="w-5 h-5" />;
  };

  // Couleur de l'icône selon le type
  const getIconColor = (name: string): string => {
    return 'text-green-700';
  };

  // Formater la date
  const formatDate = (date: Date): string => {
    return date.toLocaleDateString('fr-FR');
  };

  const GuaranteeItem: React.FC<{ guarantee: ContractGuaranteeDto }> = ({ guarantee }) => (
    <div className="flex items-center justify-between p-4 bg-white rounded-2xl border shadow transition-shadow">
      <div className="flex items-center gap-3 flex-1">
        <div className={`p-2 rounded-full bg-gray-50 ${getIconColor(guarantee.name)}`}>
          {getIcon(guarantee.name)}
        </div>

        <div className="flex-1">
          <h3 className="font-medium text-gray-900 mb-1">
            {guarantee.name}
          </h3>
          <div className="flex items-center gap-4 text-sm text-gray-500">
            <span>{guarantee.isRequired ? "Garantie de base" : "Garantie optionnelle"}</span>
            {guarantee.startDate &&
              <span>
                Date d'effet : {guarantee.isCovered ? formatDate(guarantee.startDate) : '----'}
              </span>
            }
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <span className={`text-xs rounded-full py-1 px-3 ${guarantee.isCovered
          ? 'bg-lime-100 text-lime-500'
          : 'bg-red-100 text-red-500'
          }`}>
          {guarantee.isCovered ? '✓ Couvert' : 'Non couvert'}
        </span>
        <Button color='slate' round
          onClick={() => showGuaranteeDetails(guarantee.name, guarantee.description)}
        >
          <EyeIcon className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      <CardsSection title='Garanties de base'>
        <div className="space-y-3">
          {mandatoryGuarantees.map((guarantee: ContractGuaranteeDto) => (
            <GuaranteeItem key={guarantee.id} guarantee={guarantee} />
          ))}
        </div>
      </CardsSection>
      {
        optionnalGuarantees.length > 0 &&
        <CardsSection title='Garanties optionnelles'>
          <div className="space-y-3">
            {optionnalGuarantees.map((guarantee: ContractGuaranteeDto) => (
              <GuaranteeItem key={guarantee.id} guarantee={guarantee} />
            ))}
          </div>
        </CardsSection>
      }
    </div>
  );
};

export default ContractGuarantees;
